import React from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { GameroomEvent } from '@/redux/types/room'
import { getAutoReleaseDropdownLabel } from './autoReleaseUtils'
import { RoundConfig, TimerMode } from './types'
import { ChevronDown } from 'lucide-react'
import { CharterRoundConfig } from './CharterRoundConfig'

interface MultiRoundConfigProps {
  numberOfRounds: number
  roundConfigurations: RoundConfig[]
  selectedRound: number
  events: GameroomEvent[]
  mode: TimerMode
  onNumberOfRoundsChange: (value: string, mode: TimerMode) => void
  onRoundTabChange: (index: number) => void
  onUpdateConfig: (
    index: number,
    key: keyof RoundConfig,
    value: number | boolean | number[]
  ) => void
  onApplyToAll: () => void
  disabled?: boolean
  // Round 0 (Pre-Game Charter) — first tab, shown only when charter is enabled.
  charterEnabled?: boolean
  charterGate?: boolean
  onCharterChange?: (patch: { charter_gate_enabled?: boolean }) => void
  charterSelected?: boolean
  onSelectCharter?: () => void
}

export const MultiRoundConfig: React.FC<MultiRoundConfigProps> = ({
  numberOfRounds,
  roundConfigurations,
  selectedRound,
  events,
  mode,
  onNumberOfRoundsChange,
  onRoundTabChange,
  onUpdateConfig,
  onApplyToAll,
  disabled = false,
  charterEnabled = false,
  charterGate = false,
  onCharterChange,
  charterSelected = false,
  onSelectCharter,
}) => {
  const showCharterTab = Boolean(onSelectCharter) && charterEnabled
  const isNegotiationLocked = mode === TimerMode.MULTI_ROUND_NEGOTIATION_LOCKED
  const currentRound = roundConfigurations[selectedRound]
  const selectedAutoReleaseEventIds = currentRound?.autoReleaseEventIds ?? []
  const autoReleaseDropdownLabel = getAutoReleaseDropdownLabel(
    selectedAutoReleaseEventIds,
    events
  )

  return (
    <div className='space-y-6'>
      <div className='flex flex-col space-y-2'>
        <Label htmlFor='numberOfRounds'>Number of Rounds</Label>
        <Input
          id='numberOfRounds'
          className='w-full max-w-xs'
          type='number'
          value={numberOfRounds}
          onChange={(e) => onNumberOfRoundsChange(e.target.value, mode)}
          min={1}
          max={10}
          disabled={disabled}
        />
      </div>

      <div className='space-y-4'>
        <div className='flex justify-between items-center'>
          <h3 className='text-lg font-medium'>
            Round Configurations
            {isNegotiationLocked && ' (Negotiation phase locked)'}
          </h3>
          {!disabled && !charterSelected && (
            <Button
              className='text-white bg-dark-blue hover:bg-dark-blue-hovered'
              onClick={onApplyToAll}
              size='sm'
            >
              Apply Round {selectedRound + 1} to All
            </Button>
          )}
        </div>

        <div className='rounded-md border'>
          <div className='flex border-b bg-slate-100'>
            {showCharterTab && (
              <button
                className={cn(
                  'py-2 px-4 text-sm font-medium',
                  charterSelected
                    ? 'bg-white border-r border-b-0'
                    : 'bg-slate-100 hover:bg-slate-200 border-r'
                )}
                onClick={onSelectCharter}
              >
                R0 (Pre-Game)
              </button>
            )}
            {roundConfigurations.map((_, index) => (
              <button
                key={index}
                className={cn(
                  'py-2 px-4 text-sm font-medium',
                  selectedRound === index && !charterSelected
                    ? 'bg-white border-r border-b-0'
                    : 'bg-slate-100 hover:bg-slate-200 border-r'
                )}
                onClick={() => onRoundTabChange(index)}
              >
                R{index + 1} {index === 0 ? '(RandR)' : ''}
              </button>
            ))}
          </div>

          <div className='p-4'>
            {charterSelected && charterEnabled ? (
              <CharterRoundConfig
                gate={charterGate}
                onChange={(patch) => onCharterChange?.(patch)}
                disabled={disabled}
              />
            ) : (
              <>
            <h4 className='mb-4 font-medium text-md'>
              Round {selectedRound + 1} Settings
              {selectedRound === 0 ? ' (RandR)' : ' (No RandR)'}
            </h4>

            <div className='space-y-4'>
              <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
                <Label>Event Release on Round Start</Label>

                <div className='flex items-center space-x-2'>

                  <Switch
                    id={`autoReleaseEnabled-${selectedRound}`}
                    checked={currentRound?.autoReleaseEnabled ?? false}
                    onCheckedChange={(checked) =>
                      onUpdateConfig(
                        selectedRound,
                        'autoReleaseEnabled',
                        checked
                      )
                    }
                    disabled={disabled}
                  />
                  <Label
                    htmlFor={`autoReleaseEnabled-${selectedRound}`}
                    className='text-sm text-gray-600'
                  >
                      Automatic
                  </Label>

                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type='button'
                      variant='outline'
                        size='sm'
                      disabled={disabled || !(currentRound?.autoReleaseEnabled ?? false)}
                        className='w-full justify-between bg-white px-3 h-9 text-sm'
                    >
                      <span className='truncate text-sm text-gray-600'>
                        {autoReleaseDropdownLabel}
                      </span>
                      <ChevronDown className='h-4 w-4 opacity-60' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='max-h-56 overflow-y-auto w-[200px]'>
                    {events.length === 0 ? (
                      <div className='px-3 py-2 text-sm text-gray-500'>
                        No events available
                      </div>
                    ) : (
                      events.map((event) => {
                        const isChecked =
                          selectedAutoReleaseEventIds.includes(event.id)
                        return (
                          <DropdownMenuCheckboxItem
                            key={event.id}
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              const nextIds = checked
                                ? Array.from(
                                    new Set([
                                      ...selectedAutoReleaseEventIds,
                                      event.id,
                                    ])
                                  )
                                : selectedAutoReleaseEventIds.filter(
                                    (id) => id !== event.id
                                  )

                              onUpdateConfig(
                                selectedRound,
                                'autoReleaseEventIds',
                                nextIds
                              )
                            }}
                            className='capitalize'
                          >
                            {event.title}
                          </DropdownMenuCheckboxItem>
                        )
                      })
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {mode !== TimerMode.MANUAL_MODE && (
                <>
                  {selectedRound === 0 && (
                    <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
                      <Label
                        htmlFor={`round-${selectedRound}-randrConsultation`}
                      >
                        Event Analyst Consultation
                      </Label>
                      <div className='flex items-center space-x-2'>
                        <Switch
                          id={`randrInfinite-${selectedRound}`}
                          checked={currentRound.isRandrInfinite || false}
                          onCheckedChange={(checked) =>
                            onUpdateConfig(
                              selectedRound,
                              'isRandrInfinite',
                              checked
                            )
                          }
                          disabled={disabled}
                        />
                        <Label
                          htmlFor={`randrInfinite-${selectedRound}`}
                          className='text-sm text-gray-600'
                        >
                          Infinite
                        </Label>
                      </div>
                      <Input
                        id={`round-${selectedRound}-randrConsultation`}
                        type='number'
                        value={currentRound.randrConsultation || 0}
                        onChange={(e) =>
                          onUpdateConfig(
                            selectedRound,
                            'randrConsultation',
                            parseInt(e.target.value)
                          )
                        }
                        min={1}
                        disabled={disabled || currentRound.isRandrInfinite}
                      className={cn(currentRound.isRandrInfinite && 'opacity-50')}
                      />
                      <span className='text-gray-500'>
                        {currentRound.isRandrInfinite ? '∞' : 'minutes'}
                      </span>
                    </div>
                  )}

                  <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
                    <Label
                      htmlFor={`round-${selectedRound}-advisorConsultation`}
                    >
                      Advisor Consultation
                    </Label>
                    <div className='flex items-center space-x-2'>
                      <Switch
                        id={`advisorInfinite-${selectedRound}`}
                        checked={currentRound.isAdvisorInfinite || false}
                        onCheckedChange={(checked) =>
                          onUpdateConfig(
                            selectedRound,
                            'isAdvisorInfinite',
                            checked
                          )
                        }
                        disabled={disabled}
                      />
                      <Label
                        htmlFor={`advisorInfinite-${selectedRound}`}
                        className='text-sm text-gray-600'
                      >
                        Infinite
                      </Label>
                    </div>
                    <Input
                      id={`round-${selectedRound}-advisorConsultation`}
                      type='number'
                      value={currentRound.advisorConsultation}
                      onChange={(e) =>
                        onUpdateConfig(
                          selectedRound,
                          'advisorConsultation',
                          parseInt(e.target.value)
                        )
                      }
                      min={1}
                      disabled={disabled || currentRound.isAdvisorInfinite}
                      className={cn(
                        currentRound.isAdvisorInfinite && 'opacity-50'
                      )}
                    />
                    <span className='text-gray-500'>
                      {currentRound.isAdvisorInfinite ? '∞' : 'minutes'}
                    </span>
                  </div>

                  <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
                    <Label htmlFor={`round-${selectedRound}-negotiation`}>
                      Negotiation
                    </Label>
                    <div className='flex items-center space-x-2'>
                      <Switch
                        id={`negotiationInfinite-${selectedRound}`}
                        checked={currentRound.isNegotiationInfinite || false}
                        onCheckedChange={(checked) =>
                          onUpdateConfig(
                            selectedRound,
                            'isNegotiationInfinite',
                            checked
                          )
                        }
                        disabled={
                          disabled || (isNegotiationLocked && selectedRound > 0)
                        }
                      />
                      <Label
                        htmlFor={`negotiationInfinite-${selectedRound}`}
                        className='text-sm text-gray-600'
                      >
                        Infinite
                      </Label>
                    </div>
                    <Input
                      id={`round-${selectedRound}-negotiation`}
                      type='number'
                      value={currentRound.negotiation}
                      onChange={(e) =>
                        onUpdateConfig(
                          selectedRound,
                          'negotiation',
                          parseInt(e.target.value)
                        )
                      }
                      min={1}
                      disabled={
                        disabled ||
                        currentRound.isNegotiationInfinite ||
                        (isNegotiationLocked && selectedRound > 0)
                      }
                      className={cn(
                        currentRound.isNegotiationInfinite && 'opacity-50'
                      )}
                    />
                    <span className='text-gray-500'>
                      {currentRound.isNegotiationInfinite ? '∞' : 'minutes'}
                    </span>
                  </div>

                  <div className='grid grid-cols-[1fr_auto_200px_80px] items-center gap-4 bg-slate-50 p-4 rounded-md'>
                    <Label
                      htmlFor={`round-${selectedRound}-decisionSubmission`}
                    >
                      Decision Submission
                    </Label>
                    <div className='flex items-center space-x-2'>
                      <Switch
                        id={`decisionInfinite-${selectedRound}`}
                        checked={currentRound.isDecisionInfinite || false}
                        onCheckedChange={(checked) =>
                          onUpdateConfig(
                            selectedRound,
                            'isDecisionInfinite',
                            checked
                          )
                        }
                        disabled={disabled}
                      />
                      <Label
                        htmlFor={`decisionInfinite-${selectedRound}`}
                        className='text-sm text-gray-600'
                      >
                        Infinite
                      </Label>
                    </div>
                    <Input
                      id={`round-${selectedRound}-decisionSubmission`}
                      type='number'
                      value={currentRound.decisionSubmission}
                      onChange={(e) =>
                        onUpdateConfig(
                          selectedRound,
                          'decisionSubmission',
                          parseInt(e.target.value)
                        )
                      }
                      min={1}
                      disabled={disabled || currentRound.isDecisionInfinite}
                      className={cn(
                        currentRound.isDecisionInfinite && 'opacity-50'
                      )}
                    />
                    <span className='text-gray-500'>
                      {currentRound.isDecisionInfinite ? '∞' : 'minutes'}
                    </span>
                  </div>
                </>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
